import { createModule } from "saga-slice";
import comptabiliteService from "../services/comptabiliteService";
import { call, put } from "redux-saga/effects";

const initialState = {
    // Accounts list
    comptes: [],
    clientAccounts: [],
    supplierAccounts: [],
    
    // Account statements
    releveCompte: null,
    ecrituresCompte: {
        content: [],
        totalElements: 0,
        totalPages: 0,
        number: 0,
        size: 20
    },
    ecrituresJournal: {
        content: [],
        totalElements: 0,
        totalPages: 0,
        number: 0,
        size: 20
    },
    
    // Balances and reports
    soldesComptes: [],
    balanceComptable: [],
    grandLivre: {},
    syntheseComptable: {},
    
    // Loading states
    loading: {
        comptes: false,
        clientAccounts: false,
        supplierAccounts: false,
        releveCompte: false,
        ecrituresCompte: false,
        ecrituresJournal: false,
        soldesComptes: false,
        balanceComptable: false,
        grandLivre: false,
        syntheseComptable: false
    },
    
    // Error states
    error: null
};

const comptabiliteSlice = createModule({
    name: 'comptabilite',
    initialState,
    reducers: {
        // Accounts actions
        getComptes: (state) => {
            state.loading.comptes = true;
            state.error = null;
        },
        getClientAccounts: (state) => {
            state.loading.clientAccounts = true;
            state.error = null;
        },
        getSupplierAccounts: (state) => {
            state.loading.supplierAccounts = true;
            state.error = null;
        },
        
        // Account statement actions
        getReleveCompte: (state) => {
            state.loading.releveCompte = true;
            state.error = null;
        },
        getEcrituresCompte: (state) => {
            state.loading.ecrituresCompte = true;
            state.error = null;
        },
        getEcrituresJournal: (state) => {
            state.loading.ecrituresJournal = true;
            state.error = null;
        },
        
        // Balance and report actions
        getSoldesComptes: (state) => {
            state.loading.soldesComptes = true;
            state.error = null;
        },
        getBalanceComptable: (state) => {
            state.loading.balanceComptable = true;
            state.error = null;
        },
        getGrandLivre: (state) => {
            state.loading.grandLivre = true;
            state.error = null;
        },
        getSyntheseComptable: (state) => {
            state.loading.syntheseComptable = true;
            state.error = null;
        },
        
        // Success actions
        setComptes: (state, action) => {
            state.loading.comptes = false;
            state.comptes = action;
            state.error = null;
        },
        setClientAccounts: (state, action) => {
            state.loading.clientAccounts = false;
            state.clientAccounts = action;
            state.error = null;
        },
        setSupplierAccounts: (state, action) => {
            state.loading.supplierAccounts = false;
            state.supplierAccounts = action;
            state.error = null;
        },
        setReleveCompte: (state, action) => {
            state.loading.releveCompte = false;
            state.releveCompte = action;
            state.error = null;
        },
        setEcrituresCompte: (state, action) => {
            state.loading.ecrituresCompte = false;
            state.ecrituresCompte = action;
            state.error = null;
        },
        setEcrituresJournal: (state, action) => {
            state.loading.ecrituresJournal = false;
            state.ecrituresJournal = action;
            state.error = null;
        },
        setSoldesComptes: (state, action) => {
            state.loading.soldesComptes = false;
            state.soldesComptes = action;
            state.error = null;
        },
        setBalanceComptable: (state, action) => {
            state.loading.balanceComptable = false;
            state.balanceComptable = action;
            state.error = null;
        },
        setGrandLivre: (state, action) => {
            state.loading.grandLivre = false;
            state.grandLivre = action;
            state.error = null;
        },
        setSyntheseComptable: (state, action) => {
            state.loading.syntheseComptable = false;
            state.syntheseComptable = action;
            state.error = null;
        },
        
        // Error handling
        setError: (state, action) => {
            state.loading = {
                comptes: false,
                clientAccounts: false,
                supplierAccounts: false,
                releveCompte: false,
                ecrituresCompte: false,
                ecrituresJournal: false,
                soldesComptes: false,
                balanceComptable: false,
                grandLivre: false,
                syntheseComptable: false
            };
            state.error = action || "An error occurred";
        }
    },
    sagas: (A) => ({
        *[A.getComptes]({ payload }) {
            try {
                const { search } = payload || {};
                const { data } = yield call(comptabiliteService.getComptes, search);
                yield put(A.setComptes(data.body || data));
            } catch (error) {
                yield put(A.setError(error.message));
            }
        },

        *[A.getClientAccounts]() {
            try {
                const { data } = yield call(comptabiliteService.getClientAccounts);
                yield put(A.setClientAccounts(data.body || data));
            } catch (error) {
                yield put(A.setError(error.message));
            }
        },

        *[A.getSupplierAccounts]() {
            try {
                const { data } = yield call(comptabiliteService.getSupplierAccounts);
                yield put(A.setSupplierAccounts(data.body || data));
            } catch (error) {
                yield put(A.setError(error.message));
            }
        },
        
        *[A.getReleveCompte]({ payload }) {
            try {
                const { numeroCompte, dateDebut, dateFin } = payload;
                const { data } = yield call(comptabiliteService.getReleveCompte, numeroCompte, dateDebut, dateFin);
                yield put(A.setReleveCompte(data));
            } catch (error) {
                yield put(A.setError(error.message));
            }
        },
        
        *[A.getEcrituresCompte]({ payload }) {
            try {
                const { numeroCompte, options } = payload;
                const { data } = yield call(comptabiliteService.getEcrituresCompte, numeroCompte, options);
                yield put(A.setEcrituresCompte(data.body));
            } catch (error) {
                yield put(A.setError(error.message));
            }
        },
        
        *[A.getEcrituresJournal]({ payload }) {
            try {
                const { codeJournal, options } = payload;
                const { data } = yield call(comptabiliteService.getEcrituresJournal, codeJournal, options);
                yield put(A.setEcrituresJournal(data.body));
            } catch (error) {
                yield put(A.setError(error.message));
            }
        },
        
        *[A.getSoldesComptes]({ payload }) {
            try {
                const { classeCompte, dateArrete } = payload || {};
                const { data } = yield call(comptabiliteService.getSoldesComptes, classeCompte, dateArrete);
                yield put(A.setSoldesComptes(data));
            } catch (error) {
                yield put(A.setError(error.message));
            }
        },
        
        *[A.getBalanceComptable]({ payload }) {
            try {
                const { dateDebut, dateFin } = payload || {};
                const { data } = yield call(comptabiliteService.getBalanceComptable, dateDebut, dateFin);
                yield put(A.setBalanceComptable(data));
            } catch (error) {
                yield put(A.setError(error.message));
            }
        },
        
        *[A.getGrandLivre]({ payload }) {
            try {
                const { dateDebut, dateFin, classeCompte } = payload || {};
                const { data } = yield call(comptabiliteService.getGrandLivre, dateDebut, dateFin, classeCompte);
                yield put(A.setGrandLivre(data));
            } catch (error) {
                yield put(A.setError(error.message));
            }
        },
        
        *[A.getSyntheseComptable]({ payload }) {
            try {
                const { dateDebut, dateFin } = payload || {};
                const { data } = yield call(comptabiliteService.getSyntheseComptable, dateDebut, dateFin);
                yield put(A.setSyntheseComptable(data));
            } catch (error) {
                yield put(A.setError(error.message));
            }
        }
    })
});

export default comptabiliteSlice;
export const comptabiliteActions = comptabiliteSlice.actions;
